import {AutoCompleteResult, GoogleMapsAutocomplete} from "./googleMapsAutocomplete";

// Drawn onto Google-Maps tiles / map controls, which are always light.
const MAP_ACCENT = "#2196f3"; // map-type toggle active state, geofence fill
const GEOFENCE_STROKE = "#1f334e";

export interface LocationMapConfig {
	mapElement: HTMLElement;
	mapId?: string;
	onPositionChanged?: (latLng: google.maps.LatLngLiteral) => void;
	onAutoComplete?: (result: AutoCompleteResult) => void;
	ignoreRadius?: boolean;
}

export class LocationMap {
	private static MARKER_INACTIVE_OPACITY = 0.8;
	private static DEFAULT_LOCATION = {lat: 50, lng: 15};
	private static DEFAULT_ZOOM = 4;

	private readonly map: google.maps.Map;
	private position: google.maps.LatLngLiteral | null;
	private radius?: number;
	private positionMarker: google.maps.marker.AdvancedMarkerElement;
	private locationControllable: boolean;
	private radiusCircle: google.maps.Circle;
	private positionCenterButton: JQuery;
	private autocompleteInput: JQuery;
	private autocomplete: GoogleMapsAutocomplete;
	private clickTimeout?: number;

	constructor(private config: LocationMapConfig) {
		this.map = new google.maps.Map(config.mapElement, {
			center: LocationMap.DEFAULT_LOCATION,
			zoom: LocationMap.DEFAULT_ZOOM,
			mapId: config.mapId,
			zoomControl: true,
			draggable: true,
			streetViewControl: false,
			mapTypeControl: false,
			fullscreenControl: false
		});

		this.map.addListener("click", (event: google.maps.MapMouseEvent | google.maps.IconMouseEvent) => {
			if ("placeId" in event && event.placeId != null) {
				// If placeId is present this means the user clicked an maps icon and we don't want to handle this as a click.
				return;
			}

			// When double clicking both the 'click' and 'dblclick' events are fired.
			// We set a timeout and clear it on double click to prevent handling click when the user wanted to double click.
			clearTimeout(this.clickTimeout);
			this.clickTimeout = setTimeout(() => {
				if (this.locationControllable) {
					const position = LocationMap.getEventLatLng(event);
					if (position) {
						this.setPosition(position, false);
						this.config?.onPositionChanged(position);
					}
				}
			}, 250);
		});

		this.map.addListener("dblclick", () => {
			clearTimeout(this.clickTimeout);
		});

		this.map.addListener("drag", () => {
			this.toggleMapCenteredIndication(false);
		});

		this.createMapControls();
	}

	/**
	 * Sets the position and updates the marker and radius components if necessary.
	 */
	public setPosition(position: google.maps.LatLngLiteral | null, zoomAndCenter: boolean = true) {
		this.position = position;

		if (position) {
			if (this.positionMarker) {
				this.positionMarker.position = position;
			} else {
				this.positionMarker = new google.maps.marker.AdvancedMarkerElement({
					position: position,
					title: "Task",
					gmpDraggable: this.locationControllable,
					map: this.map
				});
				this.setMarkerOpacity(this.locationControllable ? 1.0 : LocationMap.MARKER_INACTIVE_OPACITY);
				this.positionMarker.addListener("drag", () => {
					const dragPosition = this.getMarkerPosition();
					if (dragPosition) {
						this.radiusCircle?.setCenter(dragPosition);
					}
					this.toggleMapCenteredIndication(false);
				});
				this.positionMarker.addListener("dragend", () => {
					const newPosition = this.getMarkerPosition();
					if (newPosition) {
						this.position = newPosition;
						this.config?.onPositionChanged(newPosition);
					}
				});

				this.toggleCenterControlIndicator(true);
				this.refreshView();
			}

			if (zoomAndCenter) {
				this.map.setCenter(position);
				this.map.setZoom(15);
				this.toggleMapCenteredIndication(true);
			}
		} else {
			if (this.positionMarker) {
				this.positionMarker.map = null;
			}
			this.positionMarker = null;
			this.toggleCenterControlIndicator(false);
		}

		this.updateRadiusCircle();
	}

	/**
	 * Sets the location restriction radius and updates the circle component if necessary
	 */
	public setRestrictionRadius(radius: number | null | undefined) {
		this.radius = radius ?? undefined;
		this.updateRadiusCircle();
	}

	/**
	 * Updates the visibility for the autocomplete search control and whether or not the position marker is draggable
	 */
	public toggleLocationControllable(active: boolean) {
		this.locationControllable = active;
		if (this.positionMarker) {
			this.positionMarker.gmpDraggable = active;
			this.setMarkerOpacity(active ? 1.0 : LocationMap.MARKER_INACTIVE_OPACITY);
		}

		this.autocompleteInput.toggle(active);
		if (active) {
			this.refreshView();
		}
	}

	/**
	 * Fixes the map component after view changes.
	 * This needs to be called when the map itself is hidden and shown again.
	 */
	public refreshView() {
		this.map.controls[google.maps.ControlPosition.TOP_CENTER].forEach(elem => {
			this.centerControlElement(elem);
		});

		this.map.controls[google.maps.ControlPosition.BOTTOM_CENTER].forEach(elem => {
			this.centerControlElement(elem);
		});
	}

	/**
	 * Resets the map to its default location, default zoom and removes the location markers.
	 */
	public resetToDefault() {
		this.map.setCenter(LocationMap.DEFAULT_LOCATION);
		this.map.setZoom(LocationMap.DEFAULT_ZOOM);
		this.setRestrictionRadius(null);
		this.setPosition(null);
	}

	/**
	 * Safely extracts coordinates from map events that may not include a lat/lng.
	 */
	private static getEventLatLng(event: google.maps.MapMouseEvent): google.maps.LatLngLiteral | null {
		return event.latLng?.toJSON() ?? null;
	}

	private getMarkerPosition(): google.maps.LatLngLiteral | null {
		if (!this.positionMarker?.position) {
			return null;
		}

		if (this.positionMarker.position instanceof google.maps.LatLng) {
			return this.positionMarker.position.toJSON();
		}

		return this.positionMarker.position;
	}

	private setMarkerOpacity(opacity: number): void {
		if (this.positionMarker?.content instanceof HTMLElement) {
			this.positionMarker.content.style.opacity = String(opacity);
		}
	}

	/**
	 * Updates the circle radius. The circle is lazily created and destroyed when no radius or position is set.
	 */
	private updateRadiusCircle() {
		if (this.position && this.radius && !this.config.ignoreRadius) {
			if (this.radiusCircle) {
				this.radiusCircle.setCenter(this.position);
				this.radiusCircle.setRadius(this.radius);
			} else {
				this.radiusCircle = new google.maps.Circle({
					center: this.position,
					radius: this.radius,
					strokeColor: GEOFENCE_STROKE,
					strokeWeight: 2,
					strokeOpacity: 1,
					fillColor: MAP_ACCENT,
					fillOpacity: 0.2,
					clickable: false,
					map: this.map
				});
			}
		} else {
			this.radiusCircle?.setMap(null);
			this.radiusCircle = null;
		}
	}

	/**
	 * Re-centers the given custom control on the map since hiding and reshowing the elements can destroy the spacing.
	 */
	private centerControlElement(element: HTMLElement) {
		const mapDiv = this.map.getDiv();
		const space = mapDiv.clientWidth - element.clientWidth;
		element.style.left = space / 2 + "px";
	}

	/**
	 * Switches the visibility of the center control / centered indicator
	 */
	private toggleCenterControlIndicator(show: boolean) {
		this.positionCenterButton.toggle(show);
	}

	/**
	 * Toggles the primary color indication of the map centering button
	 */
	private toggleMapCenteredIndication(centered: boolean) {
		this.positionCenterButton.toggleClass("btn-primary", centered);
		this.positionCenterButton.toggleClass("btn-default", !centered);
	}

	/**
	 * Creates the custom controls for the map. This includes the autocomplete search and the lat/lng postion display.
	 */
	private createMapControls() {
		const searchDiv = document.createElement("div");
		searchDiv.style.marginTop = "10px";
		searchDiv.style.marginRight = "10px";

		this.autocomplete = new GoogleMapsAutocomplete((result) => {
			this.autocomplete.clearInput();
			this.config?.onAutoComplete(result);
		});

		this.autocomplete.setPlaceholder(messages["task.map.searchPlaceholder"]);
		const autocompleteEl = this.autocomplete.element;
		autocompleteEl.classList.add("task-location-map-autocomplete");
		autocompleteEl.style.width = "360px";
		searchDiv.append(autocompleteEl);
		this.autocompleteInput = $(autocompleteEl);

		this.map.controls[google.maps.ControlPosition.TOP_RIGHT].push(searchDiv);

		const mapTypeControlDiv = document.createElement("div");
		mapTypeControlDiv.style.marginTop = "10px";
		mapTypeControlDiv.style.marginLeft = "10px";
		mapTypeControlDiv.className = "input-group";

		const mapTypeControlBtnGroup = document.createElement("div");
		mapTypeControlBtnGroup.className = "input-group-btn";

		const buttonMap = document.createElement("button");
		buttonMap.type = "button";
		buttonMap.style.padding = "7px 12px";
		buttonMap.style.borderTopLeftRadius = "3px";
		buttonMap.style.borderBottomLeftRadius = "3px";
		buttonMap.className = "btn btn-default";
		buttonMap.textContent = messages["task.map.street"];

		buttonMap.style.backgroundColor = MAP_ACCENT;
		buttonMap.style.borderColor = MAP_ACCENT;
		buttonMap.style.color = "#fff";

		const buttonSatellite = document.createElement("button");
		buttonSatellite.type = "button";
		buttonSatellite.style.padding = "7px 12px";
		buttonSatellite.style.borderTopRightRadius = "3px";
		buttonSatellite.style.borderBottomRightRadius = "3px";
		buttonSatellite.className = "btn btn-default";
		buttonSatellite.textContent = messages["task.map.satellite"];

		buttonMap.onclick = () => {
			this.map.setMapTypeId("roadmap");
			buttonMap.style.backgroundColor = MAP_ACCENT;
			buttonMap.style.borderColor = MAP_ACCENT;
			buttonMap.style.color = "#fff";

			buttonSatellite.style.backgroundColor = null;
			buttonSatellite.style.borderColor = null;
			buttonSatellite.style.color = null;
		};

		buttonSatellite.onclick = () => {
			this.map.setMapTypeId("satellite");
			buttonSatellite.style.backgroundColor = MAP_ACCENT;
			buttonSatellite.style.borderColor = MAP_ACCENT;
			buttonSatellite.style.color = "#fff";

			buttonMap.style.backgroundColor = null;
			buttonMap.style.borderColor = null;
			buttonMap.style.color = null;
		};

		mapTypeControlBtnGroup.append(buttonMap, buttonSatellite);
		mapTypeControlDiv.append(mapTypeControlBtnGroup);
		this.map.controls[google.maps.ControlPosition.TOP_LEFT].push(mapTypeControlDiv);

		const centerMapButtonDiv = document.createElement("div");
		centerMapButtonDiv.style.marginLeft = "10px";
		centerMapButtonDiv.style.marginBottom = "10px";
		const centerMapButton = document.createElement("button");
		centerMapButton.type = "button";
		centerMapButton.title = messages["task.map.centerMap"];
		centerMapButton.style.padding = "7px 12px";
		centerMapButton.style.borderTopLeftRadius = "3px";
		centerMapButton.style.borderBottomLeftRadius = "3px";
		centerMapButton.className = "btn btn-default";
		centerMapButton.innerHTML = `<i class="fas fa-crosshairs"></i>`;

		centerMapButton.onclick = () => {
			this.map.setCenter(this.position);
			this.toggleMapCenteredIndication(true);
		};

		this.positionCenterButton = $(centerMapButton);
		centerMapButtonDiv.append(centerMapButton);

		this.map.controls[google.maps.ControlPosition.LEFT_BOTTOM].push(centerMapButtonDiv);

	}
}
